/**
 * DEBTS THE PLAN LEAVES IN PLACE, AND WHY — one note for every module's "Explain this" (owner, 22 Oct 2026: "where
 * debts exist but are not paid off, the why … is something a human auditor would want to understand. Right now
 * without this context it looks like it hasn't been addressed").
 *
 * Every module now tests each plan twice: as it is, and with the debts paid off from the savings it leaves (aged
 * care's debt twins, the village/downsizing/granny flat/cashflow "-clear-debt" twins, support at home's "and clear
 * the debts"). So the note can say, with the figures, what paying the debts off would have done to this plan:
 *   - the plan pays them off: what, and that the repayments and interest stop;
 *   - it keeps them and the twin was tested: the twin's estate, cashflow and Age Pension against this plan's;
 *   - no twin: the debts are under the floor, or the plan leaves too little to clear them as well;
 *   - a plan built by hand, or a chosen plan opened away from the tested list: where paying off is tested.
 * Nothing here decides anything; it reports what the projections already found.
 */
import React from 'react';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { cardPaymentStyleOf, isCreditCard, liabilityInterestRate } from '../../../../shared/src/calculation/support/creditCards.ts';
import { debtsWorthTesting, paidInFullLine, ZERO_INTEREST_DEBT_FLOOR } from '../../strategy/debtsToTest.ts';

/** Interest-free debts below this are not paired with a paid-off version (strategy/debtsToTest.ts, 26 Oct 2026). */
export const DEBT_TWIN_FLOOR = ZERO_INTEREST_DEBT_FLOOR;

export interface PlanFigures { estate: number; cashflow: number; pension: number }

export interface DebtsInPlanInput {
    factFind: DraftFactFind | null | undefined;
    /** What this plan pays off, in dollars (0 when it keeps every debt). */
    clearedAmount: number;
    /** This plan's figures, for the comparison. */
    plan?: PlanFigures | null;
    /** The same plan with the debts paid off, when it was tested. */
    twin?: PlanFigures | null;
    /** Years the estate is measured over. */
    years?: number;
    /**
     * Where the explanation was opened: from the tested plans (the twin is known, or known not to exist), a chosen
     * plan away from that list, or a plan built by hand.
     */
    context?: 'tested' | 'chosen' | 'manual';
}

interface Debt { description: string; balance: number; interestRate: number; paidInFull?: boolean }

/** Every debt on file: not a reverse mortgage, something owing; a card paid off monthly is flagged. */
export function debtsOnFile(factFind: DraftFactFind | null | undefined): Debt[] {
    const out: Debt[] = [];
    for (const [category, list] of Object.entries((factFind?.liabilities || {}) as Record<string, unknown>)) {
        if (!Array.isArray(list)) continue;
        for (const l of list as Array<{ type?: string; balance?: unknown; value?: unknown; description?: unknown }>) {
            if (!l || l.type === 'reverse_mortgage' || category === 'reverseMortgages') continue;
            const balance = Number(l.balance ?? l.value) || 0;
            if (!(balance > 0)) continue;
            const paidInFull = isCreditCard(l as never, category) && cardPaymentStyleOf(l as never) === 'full';
            out.push({ description: String(l.description || 'debt').trim(), balance, interestRate: liabilityInterestRate(l as never, category), ...(paidInFull ? { paidInFull: true } : {}) });
        }
    }
    return out;
}

/** What a "-clear-debt" twin pays off: the debts the engines test clearing (never a card paid off monthly). */
export function clearableDebtTotal(factFind: DraftFactFind | null | undefined): number {
    return debtsWorthTesting(debtsOnFile(factFind)).reduce((t, d) => t + d.balance, 0);
}

const signed = (n: number) => `${n >= 0 ? '+' : '−'}${formatCurrency(Math.abs(Math.round(n)))}`;

/** The note's sentences, or null where there are no debts. Exported for the tests. */
export function debtsInPlanWords(i: DebtsInPlanInput): string[] | null {
    const words = debtsInPlanSentences(i);
    // A card paid off in full each month is a monthly bill, not a decision (debtsToTest.ts): one line says so.
    const monthly = paidInFullLine(debtsOnFile(i.factFind));
    if (!words) return monthly ? [monthly] : null;
    return monthly ? [...words, monthly] : words;
}

function debtsInPlanSentences(i: DebtsInPlanInput): string[] | null {
    const debts = debtsOnFile(i.factFind).filter((d) => !d.paidInFull);
    if (!debts.length) return null;
    const v = householdVoice(i.factFind);
    const total = debts.reduce((t, d) => t + d.balance, 0);
    const list = debts.map((d) => `${formatCurrency(d.balance)} on the ${d.description}`).join(debts.length > 2 ? ', ' : ' and ');
    const many = debts.length > 1;
    const it = many ? 'them' : 'it';
    const theDebts = many ? 'the debts' : 'the debt';
    const owe = `${v.Names} ${v.you ? 'owe' : `owe${v.s}`} ${list}.`;
    const cleared = Math.max(0, Number(i.clearedAmount) || 0);
    if (cleared >= total - 1) {
        return [owe, `This plan pays ${debts.length > 1 ? 'them' : 'it'} off from savings. The repayments and the interest stop, and the money used is no longer in the bank.`];
    }
    if (cleared > 0) {
        return [owe, `This plan pays ${formatCurrency(cleared)} of it off from savings, and keeps the rest. The repayments and interest on what is paid off stop.`];
    }
    if (i.context === 'manual') {
        return [owe, `This plan keeps ${debts.length > 1 ? 'them' : 'it'}. Paying ${debts.length > 1 ? 'them' : 'it'} off can be added to a plan built by hand, and the plans we tested try each plan with the debts paid off too.`];
    }
    if (i.twin && i.plan) {
        const years = i.years ? ` after ${i.years} ${i.years === 1 ? 'year' : 'years'}` : '';
        const dEstate = i.twin.estate - i.plan.estate;
        const dCash = i.twin.cashflow - i.plan.cashflow;
        const dPension = i.twin.pension - i.plan.pension;
        const verdict = dEstate < -1
            ? `So keeping ${theDebts} leaves more to the family.`
            : dEstate > 1
                ? `Paying ${it} off leaves more to the family; that version is among the plans tested and can be chosen instead.`
                : 'Either way, what is left to the family is about the same.';
        return [
            owe,
            `This plan keeps ${debts.length > 1 ? 'them' : 'it'}. We also tested the same plan with ${debts.length > 1 ? 'the debts' : 'the debt'} paid off from savings. Against this plan, that version leaves ${Math.abs(dEstate) < 1 ? 'the same' : signed(dEstate)} to the family${years}, ${Math.abs(dCash) < 1 ? 'the same' : `${signed(dCash)} a year`} to spend and ${Math.abs(dPension) < 1 ? 'the same Age Pension' : `${signed(dPension)} a year of Age Pension`}.`,
            verdict,
        ];
    }
    if (debtsWorthTesting(debts).length === 0) {
        return [owe, `This plan keeps ${debts.length > 1 ? 'them' : 'it'}. ${many ? 'They cost' : 'It costs'} no interest, and interest-free debts under ${formatCurrency(DEBT_TWIN_FLOOR)} in total are not tested paid off beside each plan; paying ${debts.length > 1 ? 'them' : 'it'} from savings would barely move these figures.`];
    }
    if (i.context === 'chosen') {
        return [owe, `This plan keeps ${it}. The plans we tested try each plan with ${theDebts} paid off from savings as well; the version that pays ${it} off is shown there, where it could be funded.`];
    }
    return [owe, `This plan keeps ${it}. It was not tested with ${theDebts} paid off as well: what it leaves in savings, after the money kept for emergencies, does not cover ${it}.`];
}

export const DebtsInPlanNote: React.FC<DebtsInPlanInput> = (props) => {
    const words = debtsInPlanWords(props);
    if (!words) return null;
    return (
        <div className="mt-5 rounded-xl border border-slate-200 border-l-4 border-l-slate-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="debts-in-plan">
            <p className="font-bold text-slate-900">Debts</p>
            {words.map((w, i) => <p key={i} className="mt-1">{w}</p>)}
        </div>
    );
};

export default DebtsInPlanNote;
